import { CTA_VARIANT } from "../../constants/cta";
import { TEXT_ALIGNMENT } from "../../constants/design";
import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import Banner, { BANNER_TYPE, DISPLAY_TYPE } from "../banner";
import { LOCALE_AUDIENCE } from "../banner/constants";
import { Container } from "./banner.styles";
import { useBanner } from "./hooks";
import { DEFAULT_AUDIENCES_CONFIG, DISMISS_ACTIVE_BANNER_EVENT_NAME } from "./constants";
import { dispatchDismissActiveBannerEvent } from "./utils";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var BannerContext = /*#__PURE__*/createContext();
export var useBannerContext = function useBannerContext() {
  return useContext(BannerContext);
};
var BannerProvider = function BannerProvider(props) {
  var children = props.children;
  var _useBanner = useBanner(props),
    activeBanner = _useBanner.activeBanner,
    bannerHeight = _useBanner.bannerHeight,
    setBannerRef = _useBanner.setBannerRef,
    cta = _useBanner.cta,
    handleDismiss = _useBanner.handleDismiss,
    hasActiveBanner = _useBanner.hasActiveBanner,
    targetedBanners = _useBanner.targetedBanners;
  useEffect(function () {
    window.addEventListener(DISMISS_ACTIVE_BANNER_EVENT_NAME, handleDismiss);
    return function () {
      return window.removeEventListener(DISMISS_ACTIVE_BANNER_EVENT_NAME, handleDismiss);
    };
  }, [handleDismiss]);
  var value = useMemo(function () {
    return {
      bannerHeight: bannerHeight,
      hasActiveBanner: hasActiveBanner,
      activeBanner: activeBanner,
      targetedBanners: targetedBanners,
      dismissActiveBanner: handleDismiss,
      dispatchDismissActiveBannerEvent: dispatchDismissActiveBannerEvent
    };
  }, [activeBanner, bannerHeight, handleDismiss, hasActiveBanner, targetedBanners]);
  return /*#__PURE__*/_jsxs(BannerContext.Provider, {
    value: value,
    children: [!hasActiveBanner && children, hasActiveBanner && /*#__PURE__*/_jsxs(Container, {
      bannerHeight: bannerHeight,
      displayType: activeBanner.displayType,
      hasActiveBanner: hasActiveBanner,
      children: [/*#__PURE__*/_jsx(Banner, {
        backgroundColor: activeBanner.backgroundColor,
        color: activeBanner.textColor,
        cta: cta,
        ctaVariant: activeBanner.ctaButtonType,
        dismissible: activeBanner.dismissible,
        dismissButtonLabel: activeBanner.dismissButtonLabel,
        displayType: activeBanner.displayType,
        message: activeBanner.message,
        textAlign: activeBanner.textAlign,
        onDismiss: handleDismiss,
        ref: setBannerRef
      }), children]
    })]
  });
};
BannerProvider.displayName = 'BannerProvider';
BannerProvider.propTypes = {
  banners: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    dismissible: PropTypes.bool,
    dismissButtonLabel: PropTypes.string,
    displayType: PropTypes.oneOf(Object.values(DISPLAY_TYPE)),
    cta: PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    }),
    ctaButtonType: PropTypes.oneOf(Object.values(CTA_VARIANT)),
    message: PropTypes.string,
    type: PropTypes.oneOf(Object.values(BANNER_TYPE)),
    textAlign: PropTypes.oneOf(Object.values(TEXT_ALIGNMENT)),
    trackingItemName: PropTypes.string,
    renderDelay: PropTypes.number,
    audiences: PropTypes.shape({
      isAuthenticationRequired: PropTypes.oneOf([null, true, false]),
      shouldCookieBannerBeVisible: PropTypes.oneOf([null, true, false]),
      locales: PropTypes.arrayOf(PropTypes.string),
      urls: PropTypes.arrayOf(PropTypes.string),
      experimentVariations: PropTypes.arrayOf(PropTypes.string)
    })
  })),
  bannerEvents: PropTypes.func,
  children: PropTypes.node.isRequired,
  audiencesConfig: PropTypes.shape({
    isUserAuthenticated: PropTypes.oneOf([null, true, false]),
    isCookieBannerVisible: PropTypes.oneOf([null, true, false]),
    locale: PropTypes.oneOf(Object.values(LOCALE_AUDIENCE)),
    currentUrl: PropTypes.string,
    experiments: PropTypes.object
  })
};
BannerProvider.defaultProps = {
  banners: [],
  bannerEvents: function bannerEvents() {
    return {};
  },
  audiencesConfig: DEFAULT_AUDIENCES_CONFIG
};
export default BannerProvider;